<template>
  <div class="container-div">
    <v-form-select
      v-model="selectedDuration"
      @change="durationChanged"
      @keydown.native.tab="onTabNavigation"
      class="price-type-class"
      :style="selectBoxStyle"
      :aria-label="$t(`${durationNamespace}.label`)"
      ref="durationSelect"
    >
      <v-form-select-option value="DATE" v-if="showGtd">{{ getGtdTxt }}</v-form-select-option>
      <v-form-select-option value="DateText" v-if="isChangeDate">{{ goodTillStartDateText }}</v-form-select-option>
      <v-form-select-option value="DAY" v-if="showDay">{{
        $t(`${durationNamespace}.options.short.day`)
      }}</v-form-select-option>
      <v-form-select-option value="CANCEL" v-if="showGtc">{{
        $t(`${durationNamespace}.options.short.gtc`)
      }}</v-form-select-option>
      <v-form-select-option value="EXTENDED_MARKET" v-if="showGtx">
        {{ this.$t(`${durationNamespace}.options.short.dayExt`) }}</v-form-select-option
      >
    </v-form-select>
    <v-form-datepicker
      detached
      detachedClass="b-form-datepicker"
      v-if="isChangeDate"
      ref="datePicker"
      id="datePicker"
      :labelNoDateSelected="''"
      :value="fromDate"
      :date-disabled-fn="disabledDays"
      :locale="$i18n.locale"
      class="price-type-class-popup"
      :style="datePickerStyle"
      @input="onDateChanged"
      @keydown.native.tab="onShiftTabNavigation"
      :min="goodTillStartDate"
      :max="goodTillEndDate"
      button-only
    />
  </div>
</template>

<script>
import moment from 'moment';
import { fromTimezoneDate, toTimezoneDate } from '@/utils/date';
import tdHolidays from '@/utils/tdHolidays';

export default {
  name: 'duration-custom-editor',
  data() {
    return {
      selectedDuration: null,
      selectBoxStyle: {},
      datePickerStyle: {},
      fromDate: null,
      goodTillStart: 1,
      isPopupData: false,
      showDay: false,
      showGtd: false,
      showGtc: false,
      showGtx: false,
      durationNamespace: 'white-label.globalLabels.trading.duration',
      isChangeDate: false
    };
  },
  methods: {
    getValue() {
      if (this.selectedDuration === 'DATE' || this.selectedDuration === 'DateText') return this.fromDate;
      return this.selectedDuration;
    },
    getPopupPosition() {
      return 'under';
    },
    durationChanged() {
      const strategy = this.params.node.data.strategy;
      const startDate = moment(this.goodTillStartDate).format('YYYY-MM-DD');
      if (startDate !== this.fromDate) {
        this.fromDate = startDate;
        //@change event won't get triggered on 'v-model' update programmatically, hence the below call
        this.onDateChanged(this.goodTillStartDate);
      }
      if (strategy === 'MLO') {
        this.params.node.data.multilegEntry.extendedTerms.goodTill = this.selectedDuration;
        this.params.node.data.multilegEntry.extendedTerms.goodTillDate = this.fromDate;
      } else {
        this.params.node.data.extendedTerms.goodTill = this.selectedDuration;
        this.params.node.data.extendedTerms.goodTillDate = this.fromDate;
      }
      if (this.selectedDuration === 'DATE' || this.selectedDuration === 'dateText') {
        if (
          this.getGtdTxt === 'GTD' ||
          this.getGtdTxt === "Good 'till date" ||
          this.getGtdTxt === "Ordre valable jusqu'à une date donnée"
        ) {
          this.selectBoxStyle = { position: 'relative' };
        } else {
          this.selectBoxStyle = { position: 'relative' };
        }
        this.isChangeDate = true;
        this.isPopupData = true;
        this.selectedDuration = 'DateText';
        //this.fromDate = moment(this.goodTillStartDate).format('YYYY-MM-DD');
        this.$nextTick(() => {
          if (this.$refs.datePicker) this.$refs.datePicker.$el.children[0].click();
        });
      } else {
        this.isChangeDate = false;
        this.params.context.triggerInlineEditChange(this.params, this.selectedDuration);
      }
    },
    convertGoodTil(goodTill) {
      // The utc offset in goodTill comes from OS timezone and not from MM5 timezone in user settings
      return moment(fromTimezoneDate('America/Toronto', goodTill)).format('YYYY-MM-DDTHH:mm:ssZZ');
    },
    onDateChanged($event) {
      this.fromDate = moment($event).format('YYYY-MM-DD');
      const strategy = this.params.node.data.strategy;
      if (strategy === 'MLO') {
        this.params.node.data.multilegEntry.extendedTerms.goodTill = 'DATE';
        this.params.node.data.multilegEntry.extendedTerms.goodTillDate = this.fromDate;
      } else {
        this.params.node.data.extendedTerms.goodTill = 'DATE';
        this.params.node.data.extendedTerms.goodTillDate = this.fromDate;
      }
      this.params.context.triggerInlineEditChange(this.params, 'GTD');
    },
    disabledDays(value, date) {
      let disabled = false;
      if (date.getDay() === 0 || date.getDay() === 6) {
        disabled = true;
      } else {
        if (tdHolidays.isHoliday(date, this.country)) {
          disabled = true;
        }
      }
      return disabled;
    },
    createOptions() {
      let pricePlan = '';
      let orderAction = '';
      let stockType = '';
      const orderData = this.params.data;
      const isNotional = orderData.isNotional;
      const isFractional = orderData.quantity ? !Number.isInteger(orderData.quantity) : false;
      if (this.params.data.strategy === 'MLO') {
        pricePlan = this.params.data.pricePlan;
      } else {
        pricePlan = this.params.data.extendedTerms.orderType;
        orderAction = this.params.data.extendedTerms.orderAction;
      }
      stockType = this.params.data.security.type;
      let sellShortEquityOptionFlg = (orderAction !== 'SELL_SHORT' && stockType === 'EQUITY') || stockType === 'OPTION';
      if (pricePlan === 'MARKET') {
        this.showDay = true;
        if (isNotional || isFractional) {
          this.showGtc = false;
        } else {
          this.showGtd = sellShortEquityOptionFlg;
        }
      } else if (pricePlan === 'LIMIT') {
        this.showDay = true;
        if (sellShortEquityOptionFlg) {
          this.showGtd = this.showGtc = true;
        } else if (stockType === 'EQUITY') {
          this.showGtx = true;
        }
      } else if (pricePlan === 'STOP_MARKET') {
        this.showDay = true;
        if (orderAction !== 'SELL_SHORT' && stockType === 'EQUITY') {
          this.showGtd = this.showGtc = true;
        }
      } else if (pricePlan === 'STOP_LIMIT') {
        this.showDay = true;
        if (orderAction !== 'SELL_SHORT') {
          this.showGtd = this.showGtc = true;
        }
      } else if (pricePlan === 'TRAILING_STOP_MARKET' || pricePlan === 'TRAILING_STOP_LIMIT') {
        this.showDay = this.showGtd = this.showGtc = true;
      } else if (pricePlan === 'NET_DEBIT' || pricePlan === 'NET_CREDIT' || pricePlan === 'EVEN') {
        this.showDay = true;
        this.showGtd = this.showGtc = sellShortEquityOptionFlg;
      }
    },
    onTabNavigation(event) {
      if (!event.shiftKey && this.$refs.datePicker) {
        this.$refs.datePicker.$el.querySelector('button').focus();
        event.preventDefault();
        event.stopPropagation();
      } else {
        this.params.api.stopEditing();
        if (event.shiftKey) this.params.api.tabToPreviousCell();
        else this.params.api.tabToNextCell();
      }
    },
    onShiftTabNavigation(event) {
      if (event.shiftKey) {
        this.$refs.durationSelect.$el.focus();
        event.preventDefault();
        event.stopPropagation();
      } else {
        this.params.api.stopEditing();
        this.params.api.tabToNextCell();
      }
    }
  },

  created() {
    this.createOptions();
    let originalGoodTill = '';
    let originalGoodTillDate = '';
    if (this.params.data.strategy === 'MLO') {
      originalGoodTill = this.params.data.multilegEntry.extendedTerms.goodTill;
      originalGoodTillDate = this.params.data.multilegEntry.extendedTerms.goodTillDate;
    } else {
      originalGoodTill = this.params.data.extendedTerms.goodTill;
      originalGoodTillDate = this.params.data.extendedTerms.goodTillDate;
    }
    originalGoodTillDate = moment(originalGoodTillDate)
      .utcOffset(-240)
      .format('YYYY-MM-DD');
    if (originalGoodTill !== 'DATE') {
      this.isChangeDate = false;
      this.selectedDuration = originalGoodTill;
    } else {
      this.fromDate =
        originalGoodTillDate === null
          ? moment().format('YYYY-MM-DD')
          : moment(originalGoodTillDate).format('YYYY-MM-DD');
      this.selectBoxStyle = { position: 'relative' };
      this.datePickerStyle = { position: 'relative' };
      this.isPopupData = this.isChangeDate = true;
      this.selectedDuration = 'DateText';
    }
  },
  computed: {
    goodTillStartDate() {
      const currDate = toTimezoneDate(this.$store.state.applicationSettings.general.settings.timezone);
      let days = currDate.getDay() - 4;
      if (days <= 0) {
        days = 0;
      } else {
        days = 3 - days;
      }
      return moment(currDate)
        .add(days + this.goodTillStart, 'day')
        .toDate();
    },
    goodTillStartDateText() {
      return moment(this.fromDate).format('MM/DD/YYYY');
    },
    goodTillEndDate() {
      const currDate = toTimezoneDate(this.$store.state.applicationSettings.general.settings.timezone);
      return moment(currDate)
        .add(this.goodTillEnd, 'day')
        .toDate();
    },
    goodTillEnd() {
      const underlyingMarket = this.params.data.security.market || 'CA';
      if (underlyingMarket === 'CA') {
        return 90;
      } else {
        return 180;
      }
    },
    getGtdTxt() {
      return this.isChangeDate
        ? this.$t(`${this.durationNamespace}.changeDate`)
        : this.$t(`${this.durationNamespace}.options.short.gtd`);
    },
    bottomShift() {
      return 21 + (this.params.data.allTickerArray.length - 1) * 11;
    },
    topShift() {
      return Math.ceil((this.params.node.rowHeight * (this.params.data.allTickerArray.length - 1)) / 2);
    },
    colWidth() {
      return this.params.column.actualWidth;
    },
    country() {
      return this.params && this.params.data ? this.params.data.security.market : null;
    }
  }
};
</script>

<style scoped>
.price-type-class {
  width: 80%;
  float: left;
}

.price-type-class-popup {
  height: 23px;
  position: relative;
  padding-top: 1px;
  float: right;
}

.container-div {
  display: flex;
  justify-content: space-evenly;
  position: relative;
  left: -1px;
  bottom: 3px;
}
</style>
